import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Megaphone, User, PlaneTakeoff, AlertTriangle,
  CheckCircle, ChevronDown, Loader2, Send, Globe, DoorOpen
} from 'lucide-react';
import toast from 'react-hot-toast';
import { airportAnnouncementsAPI, flightsAPI, gatesAPI } from '../../services/api';
import { useMyActiveGateSession } from '../../hooks/useMyActiveGateSession';
import PageHeader from '../../components/UI/PageHeader';
import Button from '../../components/UI/Button';

const PAGING_ACTIONS = [
  { value: 'proceed_to_gate',        label: 'Proceed to gate' },
  { value: 'contact_staff',          label: 'Contact airport staff' },
  { value: 'proceed_to_checkin',     label: 'Proceed to check-in' },
  { value: 'proceed_to_immigration', label: 'Proceed to immigration' },
  { value: 'other',                  label: 'Other (specify location)' },
];

const PRIORITIES = [
  { value: 'normal',    label: 'Normal',    cls: 'border-gray-200 bg-white text-gray-700' },
  { value: 'urgent',    label: 'Urgent',    cls: 'border-orange-300 bg-orange-50 text-orange-700' },
  { value: 'emergency', label: 'Emergency', cls: 'border-red-300 bg-red-50 text-red-700' },
];

function buildPagingPreview({ passenger_name, flight_number, paging_action, action_location }) {
  if (!passenger_name) return '';
  const actionMap = {
    proceed_to_gate:        `proceed to ${action_location || '[gate]'} immediately`,
    contact_staff:          `contact airport staff at ${action_location || '[location]'} immediately`,
    proceed_to_checkin:     `proceed to the check-in counter${action_location ? ` at ${action_location}` : ''} immediately`,
    proceed_to_immigration: `proceed to immigration${action_location ? ` at ${action_location}` : ''} immediately`,
    other:                  action_location || '[location]',
  };
  const flightPart = flight_number ? ` traveling on flight ${flight_number}` : '';
  const action = actionMap[paging_action] || actionMap.other;
  return `Attention please. This is a passenger paging announcement. Would passenger ${passenger_name}${flightPart} please ${action}. We repeat, would passenger ${passenger_name}${flightPart} please ${action}. Thank you.`;
}

export default function SubmitAnnouncementPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // CR 4.3: the gate entered at session-start auto-attaches to requests made during
  // that session — the approver's target picker will pre-select it (see suggestedGate
  // handling in OperatorApprovalsPage.jsx). Falls back to the operator's own
  // in-progress session when arriving without a ?gate_id (e.g. via the bottom-tab
  // nav rather than a link from inside a session).
  const paramGateId = searchParams.get('gate_id') || null;
  const { session: mySession } = useMyActiveGateSession();
  const gateId = paramGateId || (mySession ? String(mySession.gate_id) : null);
  const [attachedGate, setAttachedGate] = useState(null);
  const [tab, setTab] = useState('zone'); // 'zone' | 'paging'

  useEffect(() => {
    if (!gateId) return;
    if (mySession && String(mySession.gate_id) === String(gateId) && mySession.gate) {
      setAttachedGate(mySession.gate);
      return;
    }
    gatesAPI.getById(gateId).then(res => setAttachedGate(res.data.data)).catch(() => {});
  }, [gateId, mySession]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Zone announcement fields
  const [zoneForm, setZoneForm] = useState({
    title: '', english_text: '', priority: 'normal',
  });

  // Paging fields
  const [pagingForm, setPagingForm] = useState({
    title: '', passenger_name: '', flight_number: '', paging_action: 'proceed_to_gate',
    action_location: '', urgency_reason: '', priority: 'urgent',
  });

  const pagingPreview = buildPagingPreview(pagingForm);

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (tab === 'zone') {
        if (!zoneForm.title.trim() || !zoneForm.english_text.trim()) {
          toast.error('Title and message text are required');
          return;
        }
        await airportAnnouncementsAPI.submit({
          announcement_type: 'zone_announcement',
          title: zoneForm.title,
          english_text: zoneForm.english_text,
          priority: zoneForm.priority,
          suggested_gate_id: gateId,
        });
      } else {
        if (!pagingForm.passenger_name.trim()) {
          toast.error('Passenger name is required');
          return;
        }
        await airportAnnouncementsAPI.submit({
          announcement_type: 'passenger_paging',
          title: pagingForm.title || `Paging: ${pagingForm.passenger_name}`,
          passenger_name: pagingForm.passenger_name,
          flight_number: pagingForm.flight_number || null,
          paging_action: pagingForm.paging_action,
          action_location: pagingForm.action_location,
          urgency_reason: pagingForm.urgency_reason,
          priority: pagingForm.priority,
          suggested_gate_id: gateId,
        });
      }
      setSubmitted(true);
    } catch (err) {
      if (!err._toasted) toast.error('Failed to submit announcement');
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle size={32} className="text-green-600" />
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">Submitted for Approval</h2>
        <p className="text-gray-500 mb-6">
          Your announcement has been sent to a supervisor for review. You'll be notified once it's approved or rejected.
        </p>
        <div className="flex gap-3 justify-center">
          <Button
            variant="primary"
            onClick={() => { setSubmitted(false); setZoneForm({ title: '', english_text: '', priority: 'normal' }); setPagingForm({ title: '', passenger_name: '', flight_number: '', paging_action: 'proceed_to_gate', action_location: '', urgency_reason: '', priority: 'urgent' }); }}
          >
            Submit another
          </Button>
          <Button variant="ghost" onClick={() => navigate('/my-announcements')}>
            My submissions
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto px-4 py-6">
      <PageHeader
        title="Submit Announcement"
        subtitle="Requests are reviewed by a supervisor before broadcast"
      />
      {gateId && (
        <div className="-mt-3 mb-5 inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 px-2.5 py-1 rounded-lg">
          <DoorOpen size={12} />
          Attached to Gate {attachedGate?.gate_code || gateId}
        </div>
      )}

      {/* Tab toggle */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded-xl mb-5">
        <button
          onClick={() => setTab('zone')}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === 'zone' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
        >
          <Megaphone size={15} /> Zone Announcement
        </button>
        <button
          onClick={() => setTab('paging')}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === 'paging' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
        >
          <User size={15} /> Passenger Paging
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {tab === 'zone' ? (
          <>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Title <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={zoneForm.title}
                onChange={e => setZoneForm(f => ({ ...f, title: e.target.value }))}
                placeholder="e.g. Flight delay notice, Baggage claim update…"
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Announcement text <span className="text-red-500">*</span>
              </label>
              <textarea
                value={zoneForm.english_text}
                onChange={e => setZoneForm(f => ({ ...f, english_text: e.target.value }))}
                rows={5}
                maxLength={1000}
                placeholder="Write the full announcement text in English. It will be translated automatically."
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
              <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                <Globe size={11} /> Translation added automatically · {zoneForm.english_text.length}/1000
              </p>
            </div>

            <PriorityPicker value={zoneForm.priority} onChange={v => setZoneForm(f => ({ ...f, priority: v }))} />
          </>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-1">Passenger name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={pagingForm.passenger_name}
                  onChange={e => setPagingForm(f => ({ ...f, passenger_name: e.target.value }))}
                  placeholder="Full name as on boarding pass"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Flight number</label>
                <input
                  type="text"
                  value={pagingForm.flight_number}
                  onChange={e => setPagingForm(f => ({ ...f, flight_number: e.target.value.toUpperCase() }))}
                  placeholder="e.g. SQ321"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Action</label>
                <div className="relative">
                  <select
                    value={pagingForm.paging_action}
                    onChange={e => setPagingForm(f => ({ ...f, paging_action: e.target.value }))}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none bg-white"
                  >
                    {PAGING_ACTIONS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                  </select>
                  <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-1">Location / Gate</label>
                <input
                  type="text"
                  value={pagingForm.action_location}
                  onChange={e => setPagingForm(f => ({ ...f, action_location: e.target.value }))}
                  placeholder="e.g. Gate F37, Information Desk T2, Counter 14"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-1">Internal note <span className="text-gray-400 font-normal">(not announced)</span></label>
                <input
                  type="text"
                  value={pagingForm.urgency_reason}
                  onChange={e => setPagingForm(f => ({ ...f, urgency_reason: e.target.value }))}
                  placeholder="e.g. Passenger missed boarding, found unattended bag…"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <PriorityPicker value={pagingForm.priority} onChange={v => setPagingForm(f => ({ ...f, priority: v }))} />

            {/* Live preview */}
            {pagingPreview && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                <p className="text-xs font-semibold text-blue-700 mb-1.5 flex items-center gap-1">
                  <Megaphone size={12} /> Announcement preview
                </p>
                <p className="text-sm text-blue-900 leading-relaxed">{pagingPreview}</p>
                <p className="text-xs text-blue-500 mt-2 flex items-center gap-1">
                  <Globe size={11} /> Will be repeated 3× with translation
                </p>
              </div>
            )}
          </>
        )}

        <Button type="submit" variant="primary" size="lg" fullWidth loading={submitting} icon={Send}>
          {submitting ? 'Submitting…' : 'Submit for approval'}
        </Button>
      </form>
    </div>
  );
}

function PriorityPicker({ value, onChange }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-2">Priority</label>
      <div className="flex gap-2">
        {PRIORITIES.map(p => (
          <button
            key={p.value}
            type="button"
            onClick={() => onChange(p.value)}
            className={`flex-1 py-2 rounded-xl border-2 text-sm font-semibold transition-all ${value === p.value ? p.cls + ' border-current ring-2 ring-offset-1 ring-current' : 'border-gray-100 bg-gray-50 text-gray-400 hover:border-gray-200'}`}
          >
            {p.value === 'emergency' && <AlertTriangle size={13} className="inline mr-1" />}
            {p.label}
          </button>
        ))}
      </div>
      {value === 'emergency' && (
        <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1">
          <AlertTriangle size={11} /> Emergency announcements interrupt all other audio immediately
        </p>
      )}
    </div>
  );
}
